import * as Sentry from '@sentry/nextjs'
import { sanitizeEventForPii, sanitizeLogForPii } from '@/lib/sentry/pii'
import { shouldStartReplay } from '@/lib/sentry/replay-gate'
import { moveAccessCodeOutOfAddressBar } from '@/lib/auth/customer/access-code-fragment'

/**
 * アラート閾値リファレンス（Sentry Dashboard > Alerts で設定する値）
 *
 * 設定手順: docs/運用/運用手順書-Sentry-警報.md
 *
 * | 指標                          | 閾値                    | 重大度    | 通知先                    |
 * | ----------------------------- | ----------------------- | --------- | ------------------------- |
 * | エラーレート                  | 1分間に10件以上         | Critical  | Google Chat #incident     |
 * | P95 レイテンシ                | 3000ms 以上             | Warning   | Google Chat #incident     |
 * | クラッシュフリーセッション率  | 99% 未満                | Critical  | Google Chat #incident     |
 */
// 🔴 はじめて用の URL の「#code=」は、Sentry が URL を読む前にアドレス欄から取り出して消す（MYP-45・Codex SO 2026-09-29）。
//    ページの URL・遷移の記録・Session Replay の録画の初めの href のどれにもコードを載せないための本体。
//    同じ画面のまま「#」だけ変わる移動でも、Sentry の受け手（popstate）より先に消すため、Sentry.init より前に登録する。
moveAccessCodeOutOfAddressBar(window)
window.addEventListener('popstate', () => moveAccessCodeOutOfAddressBar(window))
window.addEventListener('hashchange', () => moveAccessCodeOutOfAddressBar(window))

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE ?? process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  environment: process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.NODE_ENV,

  // パフォーマンス監視（サンプリングレート）
  // 2026-09-01: 1.0（全リクエスト送信）から引き下げ。
  // 実測: app/api 配下 route.ts 859本・app/ 配下 page.tsx 637本を100%サンプリングしており、
  // エラーイベントとは桁の違う量のトランザクションを送っていた。
  // 課金を増やさない方針（owner 裁定・2026-09-01）に従い一律 5% へ。
  // 🔴 経路別サンプリング（tracesSampler）は「どの経路を厚くすべきか」の業務判断が要り、
  //    拙速に決めると必要なときに薄すぎて見えない穴を作る。まず一律で止血する。
  tracesSampleRate: 0.05,

  // セッションリプレイ（本番のみ有効）
  // 2026-09-01: 0.1→0.01。エラー無しセッションの録画はコストの割に価値が低い。
  // replaysOnErrorSampleRate はエラー時のみでコストが小さく価値が高いため維持する。
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,

  // 開発環境では無効化
  enabled: process.env.NODE_ENV === 'production',

  // デバッグモード（開発時のみ）
  debug: false,

  // Pマーク対応: デフォルトの PII 送信を無効化
  sendDefaultPii: false,

  // 🔴 初めに開いた URL に秘密（はじめて用の URL の #code=・入口トークン）があるページでは Replay を始めない
  //    （MYP-45・Codex SO 2026-09-29 HIGH）。Replay は録画の初めに location.href をそのまま残し、
  //    beforeSend も beforeAddRecordingEvent もそれを伏せられない（詳しくは lib/sentry/replay-gate.ts）。
  integrations: shouldStartReplay(window.location.href)
    ? [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ]
    : [],

  beforeSend(event, hint) {
    // sanitizeEventForPii は Event を返すが beforeSend の型は ErrorEvent を期待するため as でキャスト
    return sanitizeEventForPii(event, hint) as typeof event
  },

  // 🔴 beforeSend はエラーイベント専用（Sentry SDK v10）。tracesSampleRate で送られる
  // トランザクション（ページ表示ごと）はこちらを通る。片方だけに繋ぐと
  // 「エラーは守られるがページ表示は素通り」になり、URL 中の入口トークンが社外へ出る。
  // 機構: tests/quality/sentry-transaction-scrubbing.test.ts
  // 🔴 Sentry Logs（warning の送り先・2026-09-27〜）は beforeSend を通らない。ここで同じ伏せ字をかける
  //    （公式: configuration/options の beforeSendLog）。
  beforeSendLog(log) {
    return sanitizeLogForPii(log)
  },
  beforeSendTransaction(event) {
    return sanitizeEventForPii(event) as typeof event
  },
})
